import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "./ui/sheet";
import { Separator } from "./ui/separator";
import {
  Menu,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Cpu,
  ArrowRight,
} from "lucide-react";
import { companyInfo, services, industries, navigationLinks } from "../data/mock";
import { seoPages } from "../data/seo-pages";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const NavDropdown = ({ link, isActive }) => {
  const [open, setOpen] = useState(false);
  let timeout;

  const handleEnter = () => {
    clearTimeout(timeout);
    setOpen(true);
  };
  const handleLeave = () => {
    timeout = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link
        to={link.path}
        className={`flex items-center gap-1 text-[0.9rem] font-medium transition-colors duration-200 hover:text-blue-600 ${
          isActive ? "text-blue-600" : "text-slate-700"
        }`}
      >
        {link.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </Link>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-2 dropdown-enter z-50">
          {link.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors duration-150"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileNavSection = ({ link, onClose }) => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();

  if (!link.children) {
    return (
      <Link
        to={link.path}
        onClick={onClose}
        className={`block py-3 text-base font-medium transition-colors duration-150 ${
          location.pathname === link.path
            ? "text-blue-600"
            : "text-slate-700 hover:text-blue-600"
        }`}
      >
        {link.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full py-3 text-base font-medium text-slate-700 hover:text-blue-600 transition-colors duration-150"
      >
        {link.label}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="pl-4 pb-2 space-y-1">
          <Link
            to={link.path}
            onClick={onClose}
            className="block py-2 text-sm text-blue-600 font-medium"
          >
            View All {link.label}
          </Link>
          {link.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              onClick={onClose}
              className="block py-2 text-sm text-slate-500 hover:text-blue-600 transition-colors duration-150"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top Bar */}
      <div className="hidden lg:block bg-slate-900 text-white py-2.5">
        <div className="container mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-200"
            >
              <Phone className="w-3.5 h-3.5" />
              {companyInfo.phone}
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-200"
            >
              <Mail className="w-3.5 h-3.5" />
              {companyInfo.email}
            </a>
          </div>
          <span className="flex items-center gap-2 text-slate-400">
            <MapPin className="w-3.5 h-3.5" />
            Sydney, NSW Australia
          </span>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? "glass-header shadow-sm border-slate-200/60"
            : "bg-white border-transparent"
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">
              FlexiTech
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigationLinks.map((link) => {
              const isActive =
                location.pathname === link.path ||
                (link.children &&
                  link.children.some((c) => location.pathname === c.path));

              if (link.children) {
                return (
                  <NavDropdown
                    key={link.path}
                    link={link}
                    isActive={isActive}
                  />
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[0.9rem] font-medium transition-colors duration-200 hover:text-blue-600 ${
                    isActive ? "text-blue-600" : "text-slate-700"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA + Mobile */}
          <div className="flex items-center gap-3">
            <Button
              asChild
              className="hidden lg:inline-flex bg-blue-600 hover:bg-blue-700 text-white btn-primary"
            >
              <Link to="/contact">
                Get a Quote
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-slate-700"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0">
                <div className="p-6">
                  <SheetTitle className="flex items-center gap-2.5 mb-8">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                      <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg font-bold text-slate-900">
                      FlexiTech
                    </span>
                  </SheetTitle>

                  <div className="space-y-1">
                    {navigationLinks.map((link) => (
                      <MobileNavSection
                        key={link.path}
                        link={link}
                        onClose={() => setMobileOpen(false)}
                      />
                    ))}
                  </div>

                  <Separator className="my-6" />

                  <Button
                    asChild
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Link to="/contact" onClick={() => setMobileOpen(false)}>
                      Get a Quote
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>

                  <div className="mt-6 space-y-3 text-sm text-slate-500">
                    <a
                      href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {companyInfo.phone}
                    </a>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

const Footer = () => (
  <footer className="bg-slate-900 text-white">
    <div className="container mx-auto px-6 py-16 lg:py-20">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
        {/* Company */}
        <div>
          <Link to="/" className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Cpu className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold">FlexiTech</span>
          </Link>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            Professional IT support and managed services for Australian
            businesses. Over 20 years of trusted expertise.
          </p>
          <div className="space-y-2.5 text-sm text-slate-400">
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 hover:text-white transition-colors duration-200"
            >
              <Phone className="w-4 h-4 text-blue-400" />
              {companyInfo.phone}
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 hover:text-white transition-colors duration-200"
            >
              <Mail className="w-4 h-4 text-blue-400" />
              {companyInfo.email}
            </a>
          </div>
        </div>

        {/* Services */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
            Services
          </h3>
          <ul className="space-y-2.5">
            {services.slice(0, 7).map((s) => (
              <li key={s.id}>
                <Link
                  to={`/services/${s.id}`}
                  className="text-slate-400 text-sm hover:text-white transition-colors duration-200"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Industries */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
            Industries
          </h3>
          <ul className="space-y-2.5">
            {industries.map((i) => (
              <li key={i.id}>
                <Link
                  to={`/industries/${i.id}`}
                  className="text-slate-400 text-sm hover:text-white transition-colors duration-200"
                >
                  {i.title}
                </Link>
              </li>
            ))}
          </ul>
          <Separator className="my-5 bg-slate-800" />
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-4">
            Company
          </h3>
          <ul className="space-y-2.5">
            <li>
              <Link
                to="/about"
                className="text-slate-400 text-sm hover:text-white transition-colors duration-200"
              >
                About Us
              </Link>
            </li>
            <li>
              <Link
                to="/why-flexitech"
                className="text-slate-400 text-sm hover:text-white transition-colors duration-200"
              >
                Why FlexiTech
              </Link>
            </li>
            {seoPages.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/seo/${p.id}`}
                  className="text-slate-400 text-sm hover:text-white transition-colors duration-200"
                >
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-5">
            Get In Touch
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed mb-5">
            Ready to take the stress out of IT? Let's talk about how we can help
            your business.
          </p>
          <Button
            asChild
            className="bg-blue-600 hover:bg-blue-700 text-white btn-primary"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>

      <Separator className="my-10 bg-slate-800" />

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
        <p>
          &copy; {new Date().getFullYear()} FlexiTech is operated by{" "}
          {companyInfo.legalName} (ABN {companyInfo.abn}). All rights reserved.
        </p>
        <div className="flex gap-6">
          <Link
            to="/"
            className="hover:text-white transition-colors duration-200"
          >
            Privacy Policy
          </Link>
          <Link
            to="/"
            className="hover:text-white transition-colors duration-200"
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />

      {/* Floating Phone CTA — mobile only */}
      <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40">
        <a
          href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
          className="flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full py-3.5 shadow-xl shadow-blue-600/25 transition-colors duration-200"
        >
          <Phone className="w-5 h-5" />
          <span className="font-semibold text-[0.95rem]">
            Call {companyInfo.phone}
          </span>
        </a>
      </div>
    </div>
  );
};
